(ns app.front.view.group.admin.config.number-format-editor
  "An admin editor for configurable number formats for e.g. permit and licence numbers.

  The format is stored as a template vector e.g. [\"P\" \"S\" \"YYYY\" \"S\" \"N\"]
  where each component is a placeholder for a specific value:
  P    = prefix (though can also be suffix or infix)
  S    = separator
  YY   = year with two digits
  YYYY = year with four digits
  N    = running number

  The group admin can create, remove, update and rearrange these components in the UI.
  They can also set custom inheritance rules so e.g. categories have a different prefix or
  use the same running number. See below for specifics."
  (:require [app.admin.config.util :as conf-util]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.number-format-utils :as nf-utils]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable :as ui-sortable]
            [app.front.util :as futil]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(defn safe-entity-sub
  "New unsaved configurations may use a temp entity-id. Return nil subscription in that case."
  [entity-id]
  (if (integer? entity-id)
    (rf/subscribe [::entities/entity entity-id])
    (r/atom nil)))


(defn template-components
  "Returns the available template maps, including a custom data map if a translation key is provided"
  [custom-data-tr-key]
  (concat [{:text   "P"
            :tr-key :group-admin/P}
           {:text   "S"
            :tr-key :group-admin/S}
           {:text   "YY"
            :tr-key :group-admin/YY}
           {:text   "YYYY"
            :tr-key :group-admin/YYYY}
           {:text   "N"
            :tr-key :group-admin/N}]
          (when custom-data-tr-key
            [{:text   "C"
              :tr-key custom-data-tr-key}])))

;; The editor can be either at the group level or any of the group-config levels.
;; The group-configs inherit their values from the group as well as the config tree.
;; The different levels also have different attribute namespaces (:group/x and :group.config/x)
(def is-group? (eu/is-type-fn :entity.type/group))
(def is-group-config? (eu/is-type-fn :entity.type/group-config))
(def is-group-or-config? (eu/is-type-fn :entity.type/group :entity.type/group-config))


(defn prefix-empty?
  "Returns true if the prefix would be not set (were the changes to be saved right now)"
  [prefix-attr entity]
  (let [changes       (<sub [::unsaved-changes (:db/id entity)])
        inherited-val (<sub [::inherited-value (:db/id entity) prefix-attr])]
    (if (contains? changes prefix-attr)
      (util/nil-or-blank? (get changes prefix-attr))
      (and (util/nil-or-blank? (get entity prefix-attr))
           (util/nil-or-blank? inherited-val)))))

(defn- workspace-id-year-changed?
  "Returns true if the year stored in the database for group workspace/application id counter
  is not the current year and will be updated when the next workspace id is assigned.
  Basically for checking whether the rollover (= annual reset) of the counter is about to happen.
  Only useful if municipal case id will be just the Cloudpermit workspace id and template is not used."
  [{:keys [group year-attr current-year]}]
  (and year-attr
       (some-> (get group year-attr)
               (not= current-year))))


(defn effective-counter-year [{:keys [rollover-month rollover-day group]}]
  (if (and rollover-month rollover-day
           (or (> rollover-month 1) (> rollover-day 1)))
    (let [{:keys [day
                  month ;; note: starts from 1 unlike (.getMonth date)
                  year]}                                               (date-util/format-to-parts (js/Date.) (:group/tz group))]
      (if (or (> month rollover-month)
              (and (= month rollover-month)
                   (>= day rollover-day)))
        (inc year)
        year))
    (date-util/current-year)))


(defn counter-value-at-node
  "Resolve the current case id counter value at `counter-parent` (group or group-config).
   First check if there is a case-id-counter entity child for this group or config for the effective current year,
   or `nil` year if this a perpetual counter (no rollover), then if the one-time attribute
   (e.g. :group.config/initial-case-id-counter) has been set.
   If neither is found and `counter-parent` is a group-config and has a parent, check the parent next
   for the same things.
   Finally, if we reached the root (permit type) group config, check equivalent stuff from the group itself."
  [{:keys [counter-parent counter-year counter-attr group ignore-unsaved-changes?]}]
  (let [entity-counter  (->> counter-parent
                             :case-id-counter/_parent
                             (filter #(-> % :case-id-counter/year (= counter-year)))
                             first
                             :case-id-counter/counter)
        initial-counter (get counter-parent counter-attr)]
    (or (if ignore-unsaved-changes?
          (or entity-counter
              initial-counter)
          (or (get (<sub [::unsaved-changes (:db/id counter-parent)]) counter-attr)
              entity-counter
              initial-counter))
        (when (eu/is-type? :entity.type/group-config counter-parent)
          (if-let [parent-config (:entity/parent counter-parent)]
            (counter-value-at-node (util/mmap {:counter-parent parent-config}
                                              counter-year counter-attr ignore-unsaved-changes? group))
            (counter-value-at-node (util/mmap {:counter-parent group
                                               :counter-attr   (conf-util/->group-attr counter-attr)}
                                              counter-year ignore-unsaved-changes? group)))))))


(defn counter-value
  "Get the counter value that would be next assigned to a workspace of this config. The last assigned value is stored
  in the database, so the resolved, returned value is increased by one.

  Note that when this is the value for a group-config that inherits the value from the group (use-common-counter?)
  the value used is not the currently edited value but the one currently in the db since the group
  and group-config changes are saved separately."
  [{:keys [entity entity-id group common-attr counter-attr rollover-day rollover-month ignore-unsaved-changes?]}]
  (let [get-value           (fn [attr no-inheritance?]
                              (if ignore-unsaved-changes?
                                (get entity attr)
                                (<sub [::updated-value entity-id attr no-inheritance?])))
        use-common-counter? (and common-attr
                                 (is-group-config? entity)
                                 (get-value common-attr true))
        rolls-over?         (if (and (not use-common-counter?)
                                     (is-group-config? entity)
                                     (some? (get-value :group.config/municipal-case-id-rollover true)))
                              ;; only consider gc-specific attr if it has been set to some value
                              (get-value :group.config/municipal-case-id-rollover true)
                              ;; interpret absence as true
                              (not (false? (:group/municipal-case-id-rollover group))))
        counter-year        (when rolls-over?
                              ;; year must be nil if counter does not roll over
                              (effective-counter-year (util/mmap rollover-month rollover-day group)))]
    (-> (if (or (is-group? entity)
                (not use-common-counter?))
          (counter-value-at-node (util/mmap {:counter-parent entity}
                                            counter-year counter-attr group ignore-unsaved-changes?))
          (counter-value-at-node (util/mmap {:counter-parent group
                                             :counter-attr   (conf-util/->group-attr counter-attr)}
                                            counter-year group ignore-unsaved-changes?)))
        (or 0)
        inc)))


(rf/reg-sub ::custom-counters-in-config-tree?
  ;; Returns true if there are any group-configs that have their own counter (they don't use the group's counter)
  :<- [::entities/context]
  (fn [ctx [_ group-id prefix-attr counter-attr common-attr]]
    (let [get-attr #(get %1 (conf-util/->config-attr %2))]
      (->> (entities/find-entities ctx {:group.config/group group-id
                                        :domain/state       :group-config.state/enabled})
           (filter #(and (get-attr % counter-attr)
                         (get-attr % prefix-attr)
                         (not (get-attr % common-attr))))
           seq
           boolean))))


(rf/reg-sub ::unsaved-changes
  (fn [db [_ entity-id]]
    (get-in db [::unsaved-changes entity-id])))


(rf/reg-sub ::updated-value
  ;; Returns the current (unsaved) value (see also `::inherited-value` below)
  (fn [[_ entity-id attr _no-inheritance?]]
    [(safe-entity-sub entity-id)
     (rf/subscribe [::unsaved-changes entity-id])
     (rf/subscribe [::inherited-value entity-id attr])])
  (fn [[entity changes inherited-val] [_ _ attr no-inheritance?]]
    (when attr
      (let [group-config? (eu/is-type? :entity.type/group-config entity)
            unsaved-val   (get changes attr)
            ;; E.g. common-number-seq boolean value isn't propagated through the config-tree;
            ;; only the value on the same level as the prefix is important
            inherited-val (if no-inheritance?
                            (get entity attr)
                            inherited-val)
            saved-val     (if (and group-config?
                                   (not no-inheritance?))
                            (config-model/resolve-config-value attr entity)
                            (get entity attr))
            group-val     (when group-config?
                            (get (:group.config/group entity)
                                 (conf-util/->group-attr attr)))]
        (cond
          ;; Nil unsaved-val = field emptied by user to restore inheritance
          (contains? changes attr) (if (some? unsaved-val)
                                     unsaved-val
                                     inherited-val)
          (some? saved-val) saved-val
          (some? group-val) group-val)))))


(rf/reg-sub ::inherited-value
  ;; Value can be inherited from the group if not set in the group-configs.
  ;; Unsaved inherited values are not considered since they are saved separately.
  (fn [[_ entity-id _attr]]
    (safe-entity-sub entity-id))
  (fn [entity [_ _ attr]]
    (when (and attr
               (eu/is-type? :entity.type/group-config entity))
      (let [parent-val (when-let [parent-config (:entity/parent entity)]
                         (config-model/resolve-config-value attr parent-config))]
        (if (some? parent-val)
          parent-val
          (get (:group.config/group entity)
               (conf-util/->group-attr attr)))))))


(rf/reg-sub ::template-active?
  ;; Returns true if this number format editor should be visible; i.e. if the group has custom numbers enabled
  (fn [[_ entity-id source-attr]]
    (rf/subscribe [::updated-value entity-id source-attr]))
  (fn [value]
    (= :case-id-source/custom-id value)))

(rf/reg-sub ::manual-numbering-active?
  (fn [[_ entity-id source-attr]]
    (rf/subscribe [::updated-value entity-id source-attr]))
  (fn [value]
    (= :case-id-source/manual value)))

(rf/reg-sub ::workspace-id-used?
  ;; Returns true if the workspace id is used instead of other sources of format
  (fn [[_ entity-id source-attr]]
    (rf/subscribe [::updated-value entity-id source-attr]))
  (fn [value]
    (= :case-id-source/workspace-id value)))


(rf/reg-event-db ::edit-value
  (fn [db [_ entity attr value]]
    (if (= value (get entity attr))
      (p/dissoc-in db [::unsaved-changes (:db/id entity) attr])
      (assoc-in db [::unsaved-changes (:db/id entity) attr] value))))


(rf/reg-event-db ::undo-changes
  (fn [db [_ entity-id]]
    (update db ::unsaved-changes dissoc entity-id)))


(defn- template-active?
  "Returns true if the template is used (instead of getting the value from e.g. the workspace id).
  Source values should be e.g. :case-id-source/custom-id or :licence-id-source/workspace-id etc."
  [{:keys [entity-id source-attr]}]
  (or (not source-attr)
      (<sub [::template-active? entity-id source-attr])))

(defn- manual-numbering-active?
  [{:keys [entity-id source-attr]}]
  (or (not source-attr)
      (<sub [::manual-numbering-active? entity-id source-attr])))

(defn- show-input?
  "Don't show inputs for attributes that are not included, explicitly disabled or
  are dependent on other attrs (prefix is prerequisite for the other group-config fields)"
  [attr {:keys [entity entity-id source-attr disabled-attr? prefix-attr]}]
  (and attr
       (or (not disabled-attr?)
           (not (disabled-attr? attr)))
       (or (manual-numbering-active? (util/mmap entity-id source-attr))
           (not (and (is-group-config? entity)
                     (not= attr prefix-attr)
                     (prefix-empty? prefix-attr entity))))))

(def ^:private template-error->tr-key
  {:invalid-characters              :group.config/template-warning-characters
   :year-or-prefix-required         :group.config/template-warning-year-or-prefix
   :counter-required                :group.config/template-warning-number
   :rollover-requires-year          :group.config/template-warning-rollover-wo-year
   :year-only-renewal-requires-year :group.config/template-warning-year-only-renewal-wo-year
   :year-counter-adjacent           :group.config/template-warning-separator-bw-year-and-number
   :separator-required              :group.config/template-warning-separator})

(defn- current-template-string
  "The template string as currently edited, or the default template when none has been set"
  [entity-id template-attr]
  (or (<sub [::updated-value entity-id template-attr])
      nf-utils/default-template-str))


(defn- template-error-text
  "Returns a string describing the problem if the current group template is invalid"
  [{:keys [tr entity entity-id group template-attr prefix-attr separator-attr counter-attr common-attr rollover-attr
           year-only-renewal-attr]}]
  (let [get-value       (fn [attr no-inheritance?]
                          (<sub [::updated-value entity-id attr no-inheritance?]))
        changes         (<sub [::unsaved-changes entity-id])
        template-string (current-template-string entity-id template-attr)
        errors          (set (nf-utils/validate-template
                               {:template           template-string
                                :separator          (get-value separator-attr false)
                                :rollover?          (boolean (get-value rollover-attr false))
                                :year-only-renewal? (boolean (get-value year-only-renewal-attr false))}))
        error-text      #(tr [(template-error->tr-key %)])
        common-counter? (get-value common-attr true)
        prefix          (or (get changes prefix-attr)
                            (get entity prefix-attr))]
    (cond
      (and prefix
           (-> entity
               (select-keys (keys entity))
               (merge {:db/id      entity-id
                       prefix-attr prefix
                       common-attr (or (is-group? entity)
                                       common-counter?)})
               (conf-util/case-id-prefix-in-use? group prefix-attr common-attr)))
      (tr [:error/prefix-already-in-use])

      (errors :invalid-characters) (error-text :invalid-characters)
      (errors :year-or-prefix-required) (error-text :year-or-prefix-required)
      (errors :counter-required) (error-text :counter-required)
      (errors :rollover-requires-year) (error-text :rollover-requires-year)
      (errors :year-only-renewal-requires-year) (error-text :year-only-renewal-requires-year)

      (and (is-group? entity)
           (not (str/includes? template-string "P"))
           (<sub [::custom-counters-in-config-tree? (:db/id group) prefix-attr counter-attr common-attr]))
      (tr [:group.config/template-warning-missing-prefix])

      (errors :year-counter-adjacent) (error-text :year-counter-adjacent)
      (errors :separator-required) (error-text :separator-required))))


(defn generate-number-preview
  "Uses the given template to generate a preview of the next number"
  [{:keys [tr template-attr prefix-attr separator-attr digits-attr get-value counter-value current-year]}]
  (->> (get-value template-attr nf-utils/default-template-str)
       nf-utils/template-str->template-vec
       (map #(case %
               "P" (get-value prefix-attr "")
               "S" (get-value separator-attr "")
               "YY" (-> current-year str (subs 2 4))
               "YYYY" (str current-year)
               "N" (str-util/leading-zeroes
                     counter-value
                     (get-value digits-attr 0))
               "C" (tr [:workspace/municipal-case-id])))
       str/join))


(defn- source-selector
  "Lets user select if they want to have a custom number format or use e.g. the workspace id instead"
  [{:keys [tr entity entity-id source-attr source-values source-tr-key] :as ctx}]
  (when (and (show-input? source-attr ctx)
             source-values
             source-tr-key)
    [:div.bx--row>div.bx--col-xs-12
     [ui-radio/radio
      {:name       (str entity-id "-" (name source-attr))
       :values     source-values
       :value      (<sub [::updated-value entity-id source-attr])
       :label-text (tr [source-tr-key])
       :val->str   (comp tr vector)
       :on-change  #(>evt [::edit-value entity source-attr %])}]]))

(defn info-box-1 [content]
  [:div.bx--row>div.bx--col
   [ui/help content]])

(defn- infobox
  "The guide text for this format editor, possible including a list of the inherited values for certain attributes"
  [{:keys [tr entity-id info-tr-keys info-attrs-custom info-attrs-manual counter-attr renewal-attr] :as ctx}]
  (let [info-attrs (cond (template-active? ctx) info-attrs-custom
                         (manual-numbering-active? ctx) info-attrs-manual
                         :else nil)]
    (when (or (template-active? ctx)
              (manual-numbering-active? ctx))
      [:<>
       (when (and (seq info-tr-keys)
                  (template-active? ctx))
         [info-box-1 (->> info-tr-keys
                          (map #(tr [%]))
                          (str/join " "))])
       (when info-attrs
         [:div.flex.flex-wrap.items-center
          [:div.mr-2 (str (tr [:integration-config/inherited-from-parent]) ":")]
          (futil/for-frag [attr info-attrs
                           :let [value (<sub [::inherited-value entity-id attr])]
                           :when value]
            [:div.bg-ui-blue-10.rounded.p-1.mr-2
             [:span.uppercase.text-xs.mr-2 (tr [attr])]
             [:span.font-mono
              ;; Counters are special, see `counter-value` docstring above
              (cond-> value
                (= attr counter-attr) inc
                (= attr renewal-attr) str)]])])])))

(defn component-input-impl [{:keys [type entity-id attr fallback-value on-edit class attr-tr entity tval-fn opts]}]
  (let [changes (<sub [::unsaved-changes entity-id])
        opts    (merge {:id         (str entity-id "-" (name attr))
                        :label-text (attr-tr "-lbl")
                        :value      (if (contains? changes attr)
                                      (get changes attr)
                                      (get entity attr fallback-value))
                        :on-change  (fn [e]
                                      (let [value      (tval-fn e)
                                            edit-event [::edit-value (or entity {:db/id entity-id}) attr value]]
                                        (case type
                                          (:text :number) (rf/dispatch-sync edit-event)
                                          (>evt edit-event))
                                        (when on-edit
                                          (on-edit attr value))))}
                       opts)]
    [:div {:class (or class "w-full md:w-1-4")
           :style {:min-height "40px"
                   :max-width  "300px"}}
     (case type
       :text [ui/text-input
              (-> opts
                  (merge {:placeholder (or (<sub [::inherited-value entity-id attr])
                                           (attr-tr "-ph"))})
                  (update :value str))]
       :number [ui/number-input
                (assoc opts :wrapper-class-name "w-full"
                       :hide-unit? true)]
       :boolean (cc/checkbox
                  (-> opts
                      (dissoc :placeholder)
                      (update :value boolean)
                      (set/rename-keys {:value :checked}))))]))

(defn- component-input
  "Input field for a single template component, e.g. the prefix or separator.
  Opts are passed onto the input component, except for:
  ctx      the common context map for this editor
  type     the type of input (:text, :number, or :boolean)
  attr     the attribute of the entity the input is for"
  [{:keys [type attr ctx class fallback-value] {:keys [tr entity entity-id on-edit]} :ctx :as opts}]
  (when (show-input? attr ctx)
    (let [attr-tr #(tr [(keyword "group-admin" (-> attr name (str/replace #"\?" "") (str %)))])
          tval-fn (case type
                    :text (fn [e]
                            (cond-> (futil/tval e)
                              ;; prefix must always be set for group, but it can be set to "" to clear it
                              (not= attr :group/municipal-case-id-prefix) str-util/blank->nil))
                    :number (comp futil/parse-int futil/tval)
                    :boolean identity)]
      [component-input-impl
       (util/mmap {:opts (dissoc opts :ctx :type :attr :class :fallback-value)}
                  type entity-id attr fallback-value on-edit class attr-tr entity tval-fn)])))


(defn template-str-to-selected-items [template-str template-components]
  (->> template-str
       nf-utils/template-str->template-vec
       (map-indexed (fn [idx component]
                      {:ordered/order idx
                       :text          component
                       :tr-key        (some (fn [template-component]
                                              (when (= (:text template-component) component)
                                                (:tr-key template-component)))
                                            template-components)}))))


(defn sortable-components-impl
  "Allows the user to add, remove and rearrange the template components (prefix, separator, etc.)
    Only one year or sequential number component is allowed per template."
  [{:keys [on-edit template-attr entity entity-id tr template-legend-tr-key updated-value custom-data-tr-key]}]
  (let [edit-template    #(do (when on-edit
                                (on-edit template-attr %))
                              (>evt [::edit-value (or entity {:db/id entity-id}) template-attr %]))
        template-string  (or updated-value
                             nf-utils/default-template-str)
        components       (template-components custom-data-tr-key)
        template-map-seq (template-str-to-selected-items template-string components)]
    [:div.bx--row.my-4.px-2
     [ui-sortable/sortable-items
      {:tr               tr
       :id               (str entity-id "-" (name template-attr))
       :selected         template-map-seq
       :legend-text      (tr [template-legend-tr-key])
       :values           (remove #(let [value (:text %)]
                                    (or (and (str/includes? value "Y")
                                             (str/includes? template-string "Y"))
                                        (and (str/includes? value "N")
                                             (str/includes? template-string "N"))))
                                 components)
       :val->str         #(tr [(:tr-key %)])
       :add-item         #(-> template-string (str (:text %)) edit-template)
       :remove-item      #(->> template-map-seq
                               (remove (comp (partial = %) :ordered/order))
                               (map :text)
                               str/join
                               edit-template)
       :on-order-changed #(->> % (map :text) str/join edit-template)}]]))


(defn- sortable-components
  [{:keys [tr entity entity-id on-edit template-attr template-legend-tr-key] :as ctx}]
  (when (show-input? template-attr ctx)
    [sortable-components-impl
     (util/mmap {:updated-value (<sub [::updated-value entity-id template-attr])}
                on-edit template-attr entity entity-id tr template-legend-tr-key)]))

(defn- fiscal-year-configuration
  [{:keys [entity-id on-edit rollover-month rollover-day tr]}]
  (let [month-value (or rollover-month 1)
        max-days    (inc (date-util/number-of-days-in-month (dec month-value)))
        day-value   (or rollover-day 1)]
    [:<>
     [:div
      [ui-select/select {:id           (str entity-id "-fiscal-month-select")
                         :label-text   (tr [:group.config/municipal-case-id-rollover-month])
                         :values       (range 1 13)
                         :val->str     #(tr [(keyword "date.month" (str (dec %)))])
                         :allow-empty? false
                         :value        month-value
                         :on-change    #(do (on-edit :group.config/municipal-case-id-rollover-month %)
                                            (on-edit :group.config/municipal-case-id-rollover-day 1))}]]
     [:div
      [ui-select/select {:id           (str entity-id "-fiscal-day-select")
                         :label-text   (tr [:group.config/municipal-case-id-rollover-day])
                         :values       (range 1 max-days)
                         :val->str     str
                         :allow-empty? false
                         :value        day-value
                         :on-change    (fn [v]
                                         (on-edit :group.config/municipal-case-id-rollover-day v)
                                         (when (nil? rollover-month)
                                           (on-edit :group.config/municipal-case-id-rollover-month 1)))}]]]))

(defn- on-change-fn [entity on-edit]
  (fn [attr value]
    (rf/dispatch-sync [::edit-value entity attr value])
    (when on-edit
      (on-edit attr value))))

(defn- editor
  "The editor proper, featuring the re-arrangeable template components and the fields for inputting their values"
  [{:keys [tr entity entity-id group on-edit template-attr prefix-attr separator-attr counter-attr
           year-only-renewal-attr digits-attr rollover-attr common-attr renewal-attr]
    :as   ctx}]
  (let [on-change (on-change-fn entity on-edit)]
    (if (template-active? ctx)
      (let [common-counter?        (boolean (and (is-group-config? entity)
                                                 (<sub [::updated-value entity-id common-attr true])))
            year-only-on-renewal?  (and year-only-renewal-attr
                                        (boolean (<sub [::updated-value entity-id year-only-renewal-attr])))
            ;; an enabled option stays editable so that it can be turned off, see `validate-template`
            year-only-unavailable? (and (not year-only-on-renewal?)
                                        (not (str/includes? (current-template-string entity-id template-attr) "Y")))
            group-value            #(get group (conf-util/->group-attr %))]
        [:div.flex-col
         [sortable-components ctx]
         [:div.flex.flex-wrap.items-end.gap-y-2.gap-x-4
          [component-input {:ctx ctx :type :text :attr prefix-attr}]
          [component-input {:ctx ctx :type :text :attr separator-attr}]
          ;; Counter values are special, see the docstring for `counter-value`
          [component-input {:ctx       ctx
                            :type      :number
                            :attr      counter-attr
                            :min       1
                            :disabled  common-counter?
                            :value     (counter-value ctx)
                            :on-change #(on-change counter-attr (-> % futil/tval futil/parse-int dec))}]
          [component-input
           (p/assoc-if {:ctx      ctx
                        :type     :number
                        :attr     digits-attr
                        :max      10
                        :disabled common-counter?}
                       :value (when common-counter?
                                (group-value digits-attr)))]
          ;; Here common-attr = "use common counter" = use group's counter (and related attributes)
          [component-input {:ctx       ctx
                            :type      :boolean
                            :attr      common-attr
                            :class     "mb-1"
                            :on-change #(do (on-change common-attr %)
                                            (on-change counter-attr (dec (counter-value ctx))))}]]
         (when (show-input? rollover-attr ctx)
           [:div.mt-2
            (when year-only-renewal-attr
              [:<>
               [component-input {:ctx       ctx
                                 :type      :boolean
                                 :attr      year-only-renewal-attr
                                 :class     "w-full"
                                 :value     (<sub [::updated-value entity-id year-only-renewal-attr])
                                 :on-change #(do (on-change year-only-renewal-attr %)
                                                 (when %
                                                   (on-change rollover-attr false)
                                                   (on-change renewal-attr false)))
                                 :disabled  (boolean (or (<sub [::inherited-value entity-id year-only-renewal-attr])
                                                         year-only-unavailable?))}]
               [ui/help (tr [:group-admin/municipal-case-id-year-only-on-renewal-info])]
               (when year-only-unavailable?
                 [ui/help (tr [:group.config/template-warning-year-only-renewal-wo-year])])])
            [:div.mt-2.flex.flex-wrap.items-end.xl:max-w-3-4
             [component-input
              (p/assoc-if {:ctx            ctx
                           :type           :boolean
                           :attr           rollover-attr
                           :disabled       (or year-only-on-renewal? common-counter?)
                           :on-change      #(on-change rollover-attr %)
                           :fallback-value (if (is-group? entity)
                                             ;; absence for group means that it rolls over, but for gc that it inherits group value
                                             ;; see app.domain.workspace.db-fn/allocate-municipal-case-id-txfn
                                             true
                                             (not (false? (group-value rollover-attr))))
                           :class          "w-full"}
                          :value (if year-only-on-renewal?
                                   false
                                   (when common-counter?
                                     (boolean (group-value rollover-attr)))))]
             [component-input {:ctx      ctx
                               :type     :boolean
                               :attr     renewal-attr
                               :class    "w-full"
                               :value    (<sub [::updated-value entity-id renewal-attr])
                               :disabled (boolean (or year-only-on-renewal?
                                                      (<sub [::inherited-value entity-id renewal-attr])))}]]])
         (when (eu/is-type? :entity.type/group-config entity)
           [:div.flex.flex-wrap.gap-y-2.gap-x-4
            [fiscal-year-configuration ctx]])])
      (when (manual-numbering-active? ctx)
        [component-input {:ctx      ctx
                          :type     :boolean
                          :attr     renewal-attr
                          :class    "w-1-2"
                          :value    (boolean (<sub [::updated-value entity-id renewal-attr]))
                          :disabled (boolean (<sub [::inherited-value entity-id renewal-attr]))}]))))

(defn number-preview-box [{:keys [label value]}]
  [:div.flex.mt-4
   [:div.bg-ui-blue-10.p-4.rounded-md
    [:span.mr-2 label]
    [:span.bold value]]])

(defn- number-preview
  "A preview of the next number generated according to the current (unsaved) format.
  If there's no template active, then display the default (same as workspace-id) preview"
  [{:keys [tr group current-year preview-tr-key source-attr entity-id] :as ctx}]
  (when (or (template-active? ctx)
            (not source-attr)
            (<sub [::workspace-id-used? entity-id source-attr]))
    [number-preview-box
     {:label (tr [preview-tr-key])
      :value (if (template-active? ctx)
               (generate-number-preview (assoc ctx
                                               :counter-value (counter-value ctx)
                                               :get-value (fn [attr default]
                                                            (or (<sub [::updated-value entity-id attr])
                                                                default))))
               (->> (or (when-not (workspace-id-year-changed? ctx)
                          (some-> group :group/current-application-id-counter inc))
                        1)
                    (str (:group/id group) "-" current-year "-")))}]))


(defn error-box [{:keys [error-text]}]
  (when error-text
    [:div.bx--row.mt-2>div.bx--col-xs-12>div.bg-ui-red-10.p-4.rounded-md
     error-text]))

(defn- template-error [ctx]
  [error-box {:error-text (and (template-active? ctx)
                               (template-error-text ctx))}])


(defn save-cancel-buttons
  "If :on-save is not given, state is presumed to be handled externally with :on-edit returning the
  changes from inside the component.
  Remember to handle the cancel externally as well if this is the case."
  [{:keys [tr entity-id on-save save-disabled?]}]
  (let [changes (<sub [::unsaved-changes entity-id])]
    [:div.flex.justify-end.mt-3
     [ui-action-buttons/save-cancel-buttons
      (util/mmap {:on-cancel        #(>evt [::undo-changes entity-id])
                  :on-save          on-save
                  :save-disabled?   (or (empty? changes)
                                        save-disabled?)
                  :cancel-disabled? (empty? changes)
                  :size             :small}
                 tr)]]))


(defn number-format-editor
  "Editor for a custom number format for e.g. the permit number alias municipal case id.
  Can be used for both group or group-configs and will change behavior accordingly.
  Note: If on-save has not been given, handling the state will have to be done externally with on-edit
  Options:
    tr                      standard localization function
    entity                  the group or group-config whose template we are editing
    on-edit                 fn with 2 args; attr and new value (for external state handling)
    on-save                 fn with 1 arg; a map of changes (attr->value) (for internal state handling)
    disabled-attr?          fn with 1 arg; an attribute keyword (truthy when attr is only inherited) (default #{})
    template-attr           entity attribute for the template string
    prefix-attr             entity attribute for the template prefix string
    separator-attr          entity attribute for the template separator string
    rollover-attr           entity attribute for the rollover (= yearly counter reset) boolean
    counter-attr            entity attribute for the counter integer
    digits-attr             entity attribute for the number of digits in the template for the counter
    common-attr             entity attribute for the common counter usage boolean (true = use group counters)
    renewal-attr            entity-attribute for the id renewal boolean (true = new number generated on renewal)
    year-only-renewal-attr  entity attribute for preserving the sequence and replacing only the renewal year
    year-attr               group attribute for the most recent year used for the template YY and YYYY components
    source-attr             entity attribute for the id source keyword
    source-values           values the source-attr can have
    source-tr-key           localization keyword for the source selector label
    info-tr-key             localization keyword for the template infobox
    info-attrs              list of attributes whose _inherited_ value will be listed as a guide
    template-legend-tr-key  localization keyword for the template component explanations
    preview-tr-key          localization keyword for the example number label
    rollover-month          fiscal year rollover month (group-config only)
    rollover-day            fiscal year rollover day   (group-config only)"
  [{:keys [entity disabled-attr? tr on-save rollover-month rollover-day] :as ctx}]
  {:pre [(is-group-or-config? entity)]}
  (let [entity-id (:db/id entity)
        group     (if (is-group? entity)
                    entity
                    (:group.config/group entity))
        ctx       (merge ctx
                         (util/mmap entity-id group)
                         {:current-year   (effective-counter-year (util/mmap rollover-month rollover-day group))
                          :disabled-attr? (or disabled-attr? #{})})]
    [eb/error-boundary
     [source-selector ctx]
     [infobox ctx]
     [editor ctx]
     [number-preview ctx]
     [template-error ctx]
     (when on-save
       [save-cancel-buttons (util/mmap {:save-disabled? (and (template-active? ctx)
                                                             (template-error-text ctx))
                                        :on-save        (let [changes (<sub [::unsaved-changes entity-id])]
                                                          (fn []
                                                            (on-save changes)
                                                            (>evt [::undo-changes entity-id])))}
                                       tr entity-id)])]))

(defn source-selector-tree [{:keys [entity tr source-on-edit source-attr source-values source-tr-key]}]
  (when source-values
    (let [entity-id (:db/id entity)
          on-change (fn [value]
                      (>evt [::edit-value entity :group.config/municipal-case-id-source value])
                      (when source-on-edit
                        (source-on-edit value)))]
      [:div.bx--row>div.bx--col-xs-12
       [ui-radio/radio
        {:name       (str entity-id "-" (name source-attr))
         :values     source-values
         :value      (<sub [::updated-value entity-id source-attr])
         :label-text (tr [source-tr-key])
         :val->str   (comp tr vector)
         :on-change  on-change}]])))

(defn number-format-editor-tree
  [{:keys [entity tr on-save disabled-attr? rollover-month rollover-day] :as ctx}]
  {:pre [(is-group-or-config? entity)]}
  (let [entity-id (:db/id entity)
        group     (if (is-group? entity)
                    entity
                    (:group.config/group entity))
        ctx       (merge ctx
                         (util/mmap entity-id group)
                         {:current-year   (effective-counter-year (util/mmap rollover-month rollover-day group))
                          :disabled-attr? (or disabled-attr? #{})})]
    [eb/error-boundary
     [source-selector-tree ctx]
     [infobox ctx]
     [editor ctx]
     [number-preview ctx]
     [template-error ctx]
     (when on-save
       [save-cancel-buttons (util/mmap {:save-disabled? (and (template-active? ctx)
                                                             (template-error-text ctx))
                                        :on-save        (let [changes (<sub [::unsaved-changes entity-id])]
                                                          (fn []
                                                            (on-save changes)
                                                            (>evt [::undo-changes entity-id])))}
                                       tr entity-id)])]))
